import { __awaiter } from "tslib";
import useIsPluginVersion from '@hooks/useIsPluginVersion';
import { PARSER_CATEGORIES, PARSER_RESULT_STATUS } from '@hooks/useSearchDataParser/constants';
import parser, { getQueryConfig } from '@lusha-parser/parse';
import AppDispatcher from '@reducers/app/dispatcher';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAsyncFetch } from 'react-hooks-async-handlers';
import useStateWithRef from 'react-usestateref';
import { useInterval } from 'usehooks-ts';
import { messaging } from './lib/messaging';
import { PLATFORMS } from './models/UserSettings/constants';
const TIMEOUT_DEFAULT = 1000;
const getParserPlatform = platform => {
    if (platform === PLATFORMS.LINKEDIN_RECRUITER_PRO) {
        return PLATFORMS.LINKEDIN_RECRUITER_LITE;
    }
    return platform;
};
function AppParser(props) {
    const { children } = props;
    const { app, settings } = useAppSelector(state => ({ settings: state.settings, app: state.app }));
    const { platform } = settings;
    const { url, isContentScriptReady, contentSessionId, isParserExistsForPage } = app || {};
    const [interval, setInterval] = useState(TIMEOUT_DEFAULT);
    const repeatRef = useRef(0);
    const previousContentSessionRef = useRef(contentSessionId);
    const dispatch = useAppDispatch();
    const [pluginContent, setPluginContent, pluginContentRef] = useStateWithRef();
    const isIntervalEnabled = settings.platform !== 'scout';
    const shouldStartPolling = isIntervalEnabled && isParserExistsForPage;
    // console.log('shouldStartPolling', shouldStartPolling, url, platform);
    const isPageWithoutParser = !!url && platform !== PLATFORMS.SCOUT && isParserExistsForPage === false;
    useEffect(() => {
        if (!isPageWithoutParser)
            return;
        dispatch(AppDispatcher.actions.setPageNotSupported());
    }, [isPageWithoutParser, url]);
    const isPluginHaveNewParserSupport = useIsPluginVersion('10.9.0');
    const isObserverVersion = useIsPluginVersion('10.11.0');
    const isNewParserEnabled = useMemo(() => {
        // if (platform === 'salesforceLightning' || platform === 'salesforceClassic') return isNewParserAllowed
        return isPluginHaveNewParserSupport;
    }, [isPluginHaveNewParserSupport, platform]);
    function sendPullRequest() {
        if (!shouldStartPolling)
            return null;
        if (!platform || platform === 'scout')
            return null;
        if (platform === 'salesforceLightning' || platform === 'salesforceClassic') {
            // enable for all for now
            // return null
        }
        if (isObserverVersion) {
            return null;
        }
        const query = getQueryConfig({ platform: settings.platform });
        messaging.sendMessage('CONTENT/GET', query);
    }
    function sendUrlChangedRequest() {
        if (!isParserExistsForPage)
            return null;
        if (!url)
            return null;
        const query = getQueryConfig({ platform: settings.platform });
        messaging.sendMessage('CONTENT/INIT_OBSERVER', query);
    }
    useEffect(() => {
        function onMessage(event) {
            if (!_.isObject(event.data))
                return null;
            const { type, payload } = event.data;
            switch (type) {
                case 'CONTENT/CHANGED':
                case 'CONTENT/PULL': {
                    const pluginContentCandidate = payload;
                    if (!_.isEqual(pluginContentRef.current, pluginContentCandidate)) {
                        setPluginContent(pluginContentCandidate);
                    }
                    break;
                }
                case 'SEARCH': {
                    const { html, url } = payload;
                    // scout is handled in another way already
                    if (settings.platform === 'scout')
                        return null;
                    if (isNewParserEnabled) {
                        // prevent saving html from both sources that causes extra parser triggers
                        return null;
                    }
                    setPluginContent({ html, url });
                    break;
                }
                default:
                    break;
            }
        }
        window.addEventListener('message', onMessage, false);
        return () => {
            // doesn't suppose to happen, but there is bug with 2 mounts
            window.removeEventListener('message', onMessage);
        };
    }, [isNewParserEnabled]);
    useEffect(() => {
        const isNewContentSession = previousContentSessionRef.current !== contentSessionId;
        previousContentSessionRef.current = contentSessionId;
        if (isNewContentSession || (pluginContent && url !== pluginContent.url)) {
            setPluginContent(undefined); // reset
            setInterval(TIMEOUT_DEFAULT);
            // call immediately, because interval takes extra time
            sendPullRequest();
            sendUrlChangedRequest();
        }
        else if (!pluginContent) {
            // trigger immediately when page is loaded first time
            sendPullRequest();
            sendUrlChangedRequest();
        }
    }, [url, isContentScriptReady, contentSessionId]);
    useInterval(() => {
        // first exec after 0.3s
        // auto scan page until it will be parsed
        sendPullRequest();
        repeatRef.current++;
        // prevent spamming when something went wrong
        if (repeatRef.current > 20) {
            setInterval(interval * 2);
            repeatRef.current = 0;
        }
    }, shouldStartPolling ? interval : null);
    useAsyncFetch(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!pluginContent)
            return null;
        const { url, html } = pluginContent;
        if (!html)
            return null;
        const parserResult = yield parser.parse(Object.assign(Object.assign({ platform: settings.platform }, pluginContent), { isScout: settings.platform === PLATFORMS.SCOUT, shouldMonitorValidHtml: envConfig.FEATURES.MONITOR_VALID_HTML, 
            // deprecated?
            title: '', indicatorNotFound: false }));
        console.log(dayjs().toISOString(), '[parser][result] parserResult', parserResult);
        if (!parserResult) {
            // parse unsuccessful
            return null;
        }
        if (parserResult.page_state === 'loading' || parserResult.page_state === 'empty') {
            // still need to wait page to render
            return null;
        }
        if (parserResult.clientParserError) {
            // handle response from old parser. causes sending empty searches
            return null;
        }
        const dataObj = parserResult.data || parserResult.parsedData; // only new parser references to data.
        const dataObjPrev = ((_a = app.parserResult) === null || _a === void 0 ? void 0 : _a.data) || ((_b = app.parserResult) === null || _b === void 0 ? void 0 : _b.parsedData);
        const isEqual = !!dataObj && _.isEqual(dataObj, dataObjPrev);
        if (isEqual) {
            return null;
        }
        const parserCategory = ((_c = parserResult.metadata) === null || _c === void 0 ? void 0 : _c.parserCategory) || parserResult.parserCategory || ((_d = parserResult.parsedData) === null || _d === void 0 ? void 0 : _d.parserCategory);
        parserResult.url = url;
        if (!parserResult.metadata)
            parserResult.metadata = {};
        const { metadata } = parserResult;
        if (!metadata.parserType)
            metadata.parserType = parserResult.parserPlatform;
        if (!metadata.platform)
            metadata.platform = getParserPlatform(platform);
        parserResult.parserCategory = parserCategory;
        // we need to adapt metadata until we migrate old parser
        if (parserCategory === PARSER_CATEGORIES.BULK && parserResult.metadata.contactsOnPage) {
            parserResult.metadata.contactsOnPage = parserResult.parsedData.contactsOnPage;
            parserResult.metadata.contactsParsed = parserResult.parsedData.contactsParsed;
        }
        parserResult.status = PARSER_RESULT_STATUS.SUCCESS;
        dispatch(AppDispatcher.actions.setParserResult(parserResult));
        messaging.sendMessage('PARSER/RESULT', parserResult);
    }), [pluginContent]);
    return children;
}
export default AppParser;
